/* eslint-disable camelcase */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Icon } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import {
  DateToShortReadableDate,
  FormatAsCurrencyForLocale
} from '@PetSureAustralia/petsure-react-ui-library/distLib/helpers';
import { FullPetDetailsPropTypes } from '../../../propTypes/petDetails';
import { TableFilters } from '../../filters/tableFilters';
import { GetPetIconCssClassFromPolicyNumber } from '../../../helpers/petHelpers';
import { GetTransactionTypeOptions } from '../../../helpers/transactionHelpers';
import { EnrichedTransactionsPropTypes } from '../../../propTypes/transactionPropTypes';
import { TABLE_ROW_BATCH_SIZE } from '../../../constants/constants';
import { usePaymentHistoryTable } from './usePaymentHistoryTable';
import './paymentHistoryTable.scss';

export const PaymentHistoryTable = ({
  tableId,
  petDetails,
  transactionsData,
  selectedPolicyId,
  setSelectedPolicyId
}) => {
  const navigate = useNavigate();
  const {
    transactionsToShow,
    filteredTransactions,
    activeFilters,
    today,
    lastDayOfMonth,
    minPolicyStartDate,
    isDescendingSort,
    handleSortOrderChange,
    handleFilterChange,
    handleShowMore,
    goToPayNowPage
  } = usePaymentHistoryTable({ petDetails, transactionsData, selectedPolicyId, setSelectedPolicyId });

  return (
    <>
      <section className="paymentHistoryPanel paymentHistoryPanel_table">
        <TableFilters
          petDetails={petDetails}
          onFilterChange={handleFilterChange}
          activeFilters={activeFilters}
          tableId={tableId}
          typeFilterOptions={GetTransactionTypeOptions({ transactionsData })}
          minDate={minPolicyStartDate}
          maxDate={lastDayOfMonth}
          currentDate={today}
        />

        <div className="paymentHistoryControls">
          <button
            type="button"
            onClick={handleSortOrderChange}
            className="date button text_only"
          >
            <Icon icon={isDescendingSort ? 'caret-up-solid' : 'caret-down-solid'} />
            <span className="labelText">Date</span>
          </button>

          <span className="pet">Pet</span>
          <span className="type">Type</span>
          <span className="amount">Amount</span>
        </div>

        <div className="paymentHistoryTransactions">
          {filteredTransactions.length === 0 && <span>No payments found.</span>}

          {filteredTransactions?.slice(0, transactionsToShow).map((transaction, index) => {
            const {
              Date: transactionDate,
              Pet,
              Type,
              Amount,
              FullPolicyNumber,
              Policy_InstallmentLineNumber: transactionId,
              Status
            } = transaction;

            const readableDate = DateToShortReadableDate(new Date(transactionDate));
            const petIconCssClass = GetPetIconCssClassFromPolicyNumber({
              petDetails,
              policyNumber: FullPolicyNumber
            });

            const isRejected = Status === 'Rejected';
            const isPremium = Type === 'Premium';
            const isClaimPaid = Type === 'Claim Paid';
            const transactionKey = `${FullPolicyNumber}_${transactionId || index}`;

            const dateClass = isRejected ? 'date claimPaid' : 'date';
            const petClass = isRejected ? 'pet claimPaid' : 'pet';
            const typeClass = isRejected ? 'type claimPaid' : 'type';
            const amountClass = isRejected || isClaimPaid ? 'amount claimPaid' : 'amount';
            const displayAmount = isPremium
              ? `-${FormatAsCurrencyForLocale(Amount)}`
              : FormatAsCurrencyForLocale(Amount);

            return (
              <div
                className="paymentHistoryListItem"
                key={transactionKey}
              >
                <div className="transactionRow">
                  <span className={dateClass}>{readableDate}</span>
                  <span className={petClass}>
                    <Icon icon={petIconCssClass} />
                    {Pet}
                  </span>
                  <span className={typeClass}>{Type}</span>
                  <span className={amountClass}>{displayAmount}</span>
                </div>

                {isRejected && (
                  <div className="outstandingMessage">
                    <Icon
                      icon="warning"
                      customCssClass="warningIcon"
                    />{' '}
                    We were unable to collect this premium. Please{' '}
                    <button
                      type="button"
                      onClick={() => goToPayNowPage(FullPolicyNumber)}
                      className="button primary text_only payNowLink"
                    >
                      <span className="labelText">Pay Now</span>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
      {filteredTransactions.length > 0 && filteredTransactions.length > transactionsToShow && (
        <div className="showMoreWrapper">
          <button
            type="button"
            className="button secondary"
            onClick={handleShowMore}
          >
            Show more ({Math.min(TABLE_ROW_BATCH_SIZE, filteredTransactions.length - transactionsToShow)})
          </button>
        </div>
      )}
    </>
  );
};

PaymentHistoryTable.propTypes = {
  tableId: PropTypes.string,
  petDetails: PropTypes.arrayOf(FullPetDetailsPropTypes),
  transactionsData: PropTypes.arrayOf(EnrichedTransactionsPropTypes),
  selectedPolicyId: PropTypes.string,
  setSelectedPolicyId: PropTypes.func
};
